import { useState, useEffect } from 'react'
import { collection, onSnapshot, orderBy, query, limit } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useAuth } from '../contexts/AuthContext'

/**
 * Activity event types
 */
export type ActivityEventType = 'thinking' | 'action' | 'discovery' | 'progress' | 'complete' | 'error'
export type ActivityEventIcon = 'search' | 'file' | 'sparkles' | 'check' | 'alert'

/**
 * Activity event as stored in cases/{caseId}/activity/{eventId} subcollection
 */
export interface ActivityEvent {
  id: string
  type: ActivityEventType
  message: string
  detail?: string | null
  icon: ActivityEventIcon
  timestamp: string
  created_at?: Date
}

interface UseActivityFeedResult {
  events: ActivityEvent[]
  loading: boolean
  error: Error | null
}

/**
 * Hook to subscribe to activity events from the cases/{caseId}/activity subcollection.
 * Events are ordered by timestamp (newest last for chat-like display).
 *
 * @param caseId - Case ID to subscribe to
 * @param maxEvents - Maximum number of events to fetch (default 100)
 */
export function useActivityFeed(
  caseId: string | undefined,
  maxEvents: number = 100
): UseActivityFeedResult {
  const { user } = useAuth()
  const [events, setEvents] = useState<ActivityEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!user || !caseId) {
      setEvents([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const activityRef = collection(db, 'cases', caseId, 'activity')
    const activityQuery = query(
      activityRef,
      orderBy('timestamp', 'asc'),
      limit(maxEvents)
    )

    const unsubscribe = onSnapshot(
      activityQuery,
      (snapshot) => {
        const activityEvents: ActivityEvent[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data()
          return {
            id: docSnap.id,
            type: data.type || 'progress',
            message: data.message || '',
            detail: data.detail,
            icon: data.icon || 'sparkles',
            timestamp: data.timestamp || new Date().toISOString(),
            created_at: data.created_at?.toDate?.(),
          }
        })
        setEvents(activityEvents)
        setLoading(false)
      },
      (err) => {
        console.error('Error fetching activity feed:', err)
        setError(err)
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [user, caseId, maxEvents])

  return { events, loading, error }
}
